import { useCallback, useMemo } from "react"
import _ from "lodash"
import { useVenueController } from "hooks/venue"
import { useGeoLocation } from "providers/geolocation"
import { useGetServices } from "providers/venue/modules/data/services/useGetServices"
import { IServiceConfig } from "providers/venue/types"
import { useNavigate, useSearchParams } from "react-router-dom"
import { DEFAULT_VENUE_FILTER, Q_VENUE } from "constant"
import { useVenue } from "providers/venue"
import { getFeatureIDByPath } from "utils/feature"
import config from "config"
import { canShowOnDevice } from "./utils"

interface IServiceResult {
  hasData: boolean
  services: IServiceConfig[]
  evenColServices: IServiceConfig[]
  oddColServices: IServiceConfig[]
  venueFilter: string
  locationVenueId: string
  changeVenueFilter: (venueId: string) => void
}

type UseService = () => IServiceResult

const device = config("device")

export const useService: UseService = () => {
  const navigate = useNavigate()
  const { findFeatureById, defaultVenue } = useVenue()
  const [searchParams] = useSearchParams()
  const { locationVenue } = useGeoLocation()
  const locationVenueId = _.get(locationVenue, "id")
  const { viewingVenue: venueFilter } = useVenueController(
    defaultVenue?.id || locationVenueId
  )

  const { data: services = [] } = useGetServices()

  const servicesFilteredByVenue = useMemo(() => {
    return _.compact(
      services.map((service) => {
        if (!canShowOnDevice(service.target_device, device)) return null

        const isAllVenue = venueFilter === DEFAULT_VENUE_FILTER
        const serviceVenueId = service.venue_id
        // Return null if the service is tied to a specific venue that doesn't match the current venue filter — to be filtered out later.
        if (!isAllVenue && serviceVenueId && serviceVenueId !== venueFilter)
          return null

        let filteredLocation = service.locations

        if (!isAllVenue) {
          filteredLocation = filteredLocation.filter((location) => {
            const featureId = getFeatureIDByPath(location.url)
            const feature = findFeatureById(featureId)

            // Keep locations that do not use a feature ID.
            if (!feature) return true

            // Filter locations by the specified venue filter ID.
            return feature?.properties?.venue_id === venueFilter
          })

          // Return null if no service locations match the venue filter — will be excluded in the next step.
          if (filteredLocation.length === 0) return null
        }

        return {
          ...service,
          locations: filteredLocation,
        }
      })
    )
  }, [venueFilter, findFeatureById, services])

  const [evenColServices, oddColServices] = useMemo(() => {
    return servicesFilteredByVenue.reduce(
      (accumulator, currentValue, index) => {
        accumulator[index % 2].push(currentValue)
        return accumulator
      },
      [[], []]
    )
  }, [servicesFilteredByVenue])

  const hasData = _.size(servicesFilteredByVenue) > 0

  const changeVenueFilter = useCallback(
    (venueId) => {
      if (venueId) searchParams.set(Q_VENUE, venueId)
      navigate(`/services/?${searchParams.toString()}`, { replace: true })
    },
    [navigate, searchParams]
  )

  return {
    services: servicesFilteredByVenue,
    evenColServices,
    oddColServices,
    hasData,
    venueFilter,
    locationVenueId,
    changeVenueFilter,
  }
}
